import { useTranslation } from 'react-i18next'
import { TextField, Autocomplete } from '@mui/material'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { useDataApi } from '../../context/DataApiContext'
import { useNotificationsProvider } from '../../context/NotificationsContext'

export default function AutoCompleteInput({
  onChange,
  label,
  resource,
  value,
  defaultValue,
  required,
  error,
  query,
  refreshOnInputChange,
  multiple,
  getOptionLabel,
  disabled,
  freeSolo,
  onInputChange,
  canDelete,
  onKeyDown,
}) {
  const { t } = useTranslation()
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()

  const [loading, setLoading] = useState(false)
  const [options, setOptions] = useState([])
  const [optionsReceived, setOptionsReceived] = useState({
    state: false,
    query: null,
  })

  // Track the last query string the component fetched against. Using a ref
  // (not state) means the comparison doesn't get tangled with React's
  // batching/closure timing — the value is always observed and written
  // synchronously, regardless of when the effect runs.
  const lastFetchedQueryRef = useRef(null)
  const hasOpenedRef = useRef(false)

  let timerID
  const WAIT_INTERVAL = 2500

  const getOptions = (text) => {
    if (refreshOnInputChange && (!text || text.trim().length === 0)) {
      setLoading(false)
      setOptionsReceived({
        state: true,
        query,
      })
      return
    }

    const effectiveQuery = { q: text, ...query }
    lastFetchedQueryRef.current = JSON.stringify(query || null)
    dataProvider
      .get(`${resource}`, { query: effectiveQuery })
      .then((response) => setOptions(response.data))
      .catch(() => showNotification('error', t('errors.api-error')))
      .then(() => {
        setLoading(false)
        setOptionsReceived({
          state: true,
          query,
        })
      })
  }

  useEffect(() => {
    // Compare by value, not reference — parents always rebuild the `query`
    // object literal on every render, so a reference-only check would
    // trigger on every keystroke in an unrelated filter. We only want to
    // act on genuine value changes, and only after the user has opened the
    // dropdown at least once (otherwise the initial fetch is deferred to
    // onOpen).
    const queryStr = JSON.stringify(query || null)
    if (queryStr === lastFetchedQueryRef.current) return
    if (!hasOpenedRef.current) return
    setOptions([])
    setLoading(true)
    getOptions()
  }, [query])

  // These 2 functions are used to fetch all options when the component mounts to use on a multiple select field to fetch the titles of the selected IDs
  // Fetch all options when the component mounts or query changes
  useEffect(() => {
    const fetchOptions = async () => {
      setLoading(true)
      try {
        const response = await dataProvider.get(resource, { query })
        setOptions(response.data) // Assume the API returns an array of objects with `id` and `title`
      } catch (error) {
        showNotification('error', t('errors.api-error'))
      } finally {
        setLoading(false)
      }
    }

    if (multiple === true && value && value.length > 0) {
      fetchOptions()
    }
  }, [resource, query, dataProvider, showNotification, t])

  // Ensure value is synced with options (add missing titles)
  const resolvedValue = useCallback(() => {
    if (!value || value.length === 0 || !options) return []

    // Map selected IDs to their full objects
    let mappedValues = value.map((selectedItem) => {
      const match = options.find((option) => option.id === selectedItem.id)
      return match || selectedItem // Use option if found, fallback to original
    })

    // Verify if page was refreshed by checking if titles are available or not
    if (
      mappedValues.some((item) => item.title) &&
      value.some((item) => !item.title) // Only updated once. If mappedValues have titles and the value still does not have titles.
    ) {
      onChange(mappedValues) // Update parent state with full objects
    }
    return mappedValues
  }, [value, options])

  const getChipProps = () => {
    if (canDelete === false) {
      return {
        deleteIcon: null,
        onDelete: null,
      }
    }
    return null
  }

  const buildOptionLabel = useCallback((option) => {
    if (!option) {
      return ''
    }
    if (getOptionLabel) {
      return getOptionLabel(option)
    }
    return option.title || ''
  })

  return (
    <Autocomplete
      id={`autocomple-${resource}`}
      loading={loading}
      loadingText={t('common.loading')}
      options={options}
      disabled={disabled}
      freeSolo={freeSolo}
      getOptionLabel={buildOptionLabel}
      noOptionsText={t('common.noOptions')}
      value={multiple === true ? resolvedValue() : value} // Resolved value includes full objects
      defaultValue={defaultValue}
      multiple={multiple === true}
      disableClearable={canDelete === false}
      clearOnEscape={canDelete}
      ChipProps={getChipProps()}
      onChange={(event, newValue, reason) => {
        if (canDelete === true) {
          onChange(newValue)
        } else if (canDelete === false && reason !== 'removeOption') {
          onChange(newValue)
        } else {
          onChange(value)
        }
      }}
      renderOption={(props, option) => {
        const customKey = option.id || props.key
        return (
          <li {...props} key={customKey}>
            {buildOptionLabel(option)}
          </li>
        )
      }}
      renderInput={(params) => (
        <TextField
          variant="standard"
          error={error}
          required={required}
          helperText={required ? t('common.required') : ''}
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          onKeyDown={(e) => {
            if (onKeyDown) {
              onKeyDown(e)
            }
          }}
        />
      )}
      onInputChange={(event, newTextValue) => {
        if (refreshOnInputChange) {
          setLoading(true)
          clearTimeout(timerID)
          timerID = setTimeout(() => {
            getOptions(newTextValue)
          }, WAIT_INTERVAL)
        }
        if (onInputChange) {
          onInputChange(newTextValue)
        }
      }}
      onOpen={() => {
        hasOpenedRef.current = true
        if (!optionsReceived.state && !loading) {
          setLoading(true)
          clearTimeout(timerID)
          getOptions()
        }
      }}
      isOptionEqualToValue={(option, val) => {
        if (typeof option.id !== 'undefined' && option.id !== null) {
          return option.id === val.id
        }
        return option === val
      }}
    />
  )
}
